import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { integrationsApi, type BaseIntegrationStatus } from '@/modules/settings/api/integrationsApi';
import { integrationKeys } from '@/modules/settings/queryKeys';
import { lifecycleApi } from '@/modules/strategy/api/lifecycleApi';
import { strategyKeys } from '@/modules/strategy/queryKeys';
import { campaignsApi } from '../api/campaignsApi';
import { campaignKeys } from '../queryKeys';

/**
 * Ads module state — location-scoped, surfaced as independent dimensions rather
 * than a single collapsed enum. Each page derives its own behavior from the
 * combination of dimensions (see the scenario matrix in the Ad Campaigns docs):
 *
 *  - strategy   — strategy lifecycle status
 *  - campaigns  — campaign pipeline state (PN + imported, unified)
 *  - accounts   — which ad platforms are connected
 *
 * Plus two derived convenience fields (navLocked, mature) so consumers don't each
 * recompute them.
 */
export type StrategyDimension = 'not_started' | 'in_progress' | 'active';
export type CampaignsDimension = 'none' | 'recommendations' | 'active';

/** An ad platform whose link is in status 'error' (e.g. auth failed). */
export interface AdsFailure {
  platform: 'Google Ads' | 'Meta Ads';
  reason: string;
  /** What to do about it, as a full sentence. */
  fix: string;
}

export interface AdAccounts {
  google: boolean;
  meta: boolean;
  any: boolean;
  both: boolean;
  /** Platforms in status 'error'. Does not make `any` true. */
  failed: AdsFailure[];
  /** Nothing is connected and at least one platform failed: show the failure, not the Connect screen. */
  connectionFailed: boolean;
}

const NO_REASON = 'The last sync could not read this account.';
const GOOGLE_FIX = 'Reconnect it in Settings → Integrations.';
const META_FIX = 'Your agency needs to reconnect it.';

/** The stored error as a sentence ending in one full stop. */
const sentence = (reason: string | null | undefined) => `${(reason || NO_REASON).replace(/[.\s]+$/, '')}.`;

/** Collect the ad platforms whose connection is in status 'error', with the reason. */
function adsFailures(
  googleStatus: BaseIntegrationStatus | undefined,
  metaStatus: BaseIntegrationStatus | undefined,
): AdsFailure[] {
  const failures: AdsFailure[] = [];
  if (googleStatus?.status === 'error') {
    failures.push({ platform: 'Google Ads', reason: sentence(googleStatus.lastSyncError), fix: GOOGLE_FIX });
  }
  if (metaStatus?.status === 'error') {
    failures.push({ platform: 'Meta Ads', reason: sentence(metaStatus.lastSyncError), fix: META_FIX });
  }
  return failures;
}

export interface AdsState {
  /** False until every underlying query has resolved. */
  loaded: boolean;
  /** True once the two connection-status queries have settled (failed ones count), whatever the other queries do. */
  accountsLoaded: boolean;
  strategy: StrategyDimension;
  campaigns: CampaignsDimension;
  accounts: AdAccounts;
  /** Lock the Campaigns/Performance/Activity sub-pages: no account connected and
   *  nothing to act on. Recommendations stay unlocked even with no account so the
   *  user can review them before connecting. */
  navLocked: boolean;
  /** ≥1 PN campaign live >30 days — switches some copy from onboarding framing to
   *  ongoing-insights framing. */
  mature: boolean;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/** PN campaign statuses that count as an in-flight recommendation pipeline. */
const PIPELINE_STATUSES = [
  'recommended',
  'draft',
  'generating',
  'pending_creative',
  'ready_for_review',
  'approved',
  'launching',
];

function useAdsQueries() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  const { data: googleStatus, isLoading: googleLoading } = useQuery({
    queryKey: integrationKeys.googleAdsStatus(locationId),
    queryFn: () => integrationsApi.googleAds.getStatus().then((r) => r.data),
    enabled: !!locationId,
    staleTime: 60_000,
  });

  const { data: metaStatus, isLoading: metaLoading } = useQuery({
    queryKey: integrationKeys.metaAdsStatus(locationId),
    queryFn: () => integrationsApi.metaAds.getStatus(locationId).then((r) => r.data),
    enabled: !!locationId,
    staleTime: 60_000,
  });

  const { data: lifecycleData } = useQuery({
    queryKey: strategyKeys.lifecycle(locationId),
    queryFn: () => lifecycleApi.getLifecycle(locationId),
    enabled: !!locationId,
    staleTime: 60_000,
  });

  const { data: campaignsData } = useQuery({
    queryKey: campaignKeys.marketingByLocation(locationId),
    queryFn: () => campaignsApi.list(locationId),
    enabled: !!locationId,
    staleTime: 60_000,
  });

  return { locationId, googleStatus, metaStatus, lifecycleData, campaignsData, accountsLoaded: !googleLoading && !metaLoading };
}

const EMPTY_STATE: AdsState = {
  loaded: false,
  accountsLoaded: false,
  strategy: 'not_started',
  campaigns: 'none',
  accounts: { google: false, meta: false, any: false, both: false, failed: [], connectionFailed: false },
  navLocked: false,
  mature: false,
};

export function useAdsState(): AdsState {
  const { locationId, googleStatus, metaStatus, lifecycleData, campaignsData, accountsLoaded } = useAdsQueries();

  // The connection statuses alone, so a page that needs only those is not held up by the other queries.
  const google = !!(googleStatus?.connected && googleStatus.status === 'connected');
  const meta = !!(metaStatus?.connected && metaStatus.status === 'connected');
  const failed = adsFailures(googleStatus, metaStatus);
  const accounts: AdAccounts = {
    google,
    meta,
    any: google || meta,
    both: google && meta,
    failed,
    connectionFailed: !(google || meta) && failed.length > 0,
  };

  if (!locationId || !googleStatus || !metaStatus || !lifecycleData || !campaignsData) {
    return { ...EMPTY_STATE, accountsLoaded, accounts };
  }

  // Strategy: read off the status column. `active` keys off the approved version
  // (`active`, not the latest), so a draft refresh on top of a still-active prior
  // version still reads as active rather than in_progress.
  const lifecycle = lifecycleData.data;
  const strategy: StrategyDimension =
    lifecycle?.active != null
      ? 'active'
      : lifecycle?.strategy?.status === 'in_progress'
        ? 'in_progress'
        : 'not_started';

  // Campaigns: unified across sources, excluding dismissed. Live (any source) wins;
  // otherwise a PN campaign still in the pipeline counts as recommendations.
  // (`list(locationId)` is already scoped to the active location server-side.)
  const campaignList = (campaignsData.data ?? []).filter((c) => c.status !== 'dismissed');
  const hasLive = campaignList.some((c) => c.status === 'live');
  const hasPnPipeline = campaignList.some(
    (c) => c.source === 'pn-ai' && PIPELINE_STATUSES.includes(c.status),
  );
  const campaigns: CampaignsDimension = hasLive
    ? 'active'
    : hasPnPipeline
      ? 'recommendations'
      : 'none';

  const mature = campaignList.some((c) => {
    if (c.source === 'external') return false;
    const launched = (c as { launchedAt?: string | null }).launchedAt;
    if (!launched) return false;
    return Date.now() - new Date(launched).getTime() > THIRTY_DAYS_MS;
  });

  return {
    loaded: true,
    accountsLoaded,
    strategy,
    campaigns,
    accounts,
    // A failed connection keeps the pages open so they can show the reason.
    navLocked: !accounts.any && !accounts.connectionFailed && campaigns === 'none',
    mature,
  };
}
